import type { NetworkType } from '../types';

export interface NetworkConfig {
  name: string;
  chainId: number;
  coinType: number; // BIP-44 coin type
  rpcUrl: string;
  explorerUrl: string;
  nativeCurrency: {
    name: string;
    symbol: string;
    decimals: number;
  };
  isEvm: boolean;
}

export const NETWORKS: Record<NetworkType, NetworkConfig> = {
  bitcoin: {
    name: 'Bitcoin',
    chainId: 0,
    coinType: 0, // m/44'/0'
    rpcUrl: 'https://blockstream.info/api',
    explorerUrl: 'https://blockstream.info',
    nativeCurrency: {
      name: 'Bitcoin',
      symbol: 'BTC',
      decimals: 8,
    },
    isEvm: false,
  },
  ethereum: {
    name: 'Ethereum',
    chainId: 1,
    coinType: 60, // m/44'/60'
    rpcUrl: 'https://eth.llamarpc.com',
    explorerUrl: 'https://etherscan.io',
    nativeCurrency: {
      name: 'Ether',
      symbol: 'ETH',
      decimals: 18,
    },
    isEvm: true,
  },
  ton: {
    name: 'TON',
    chainId: -239,
    coinType: 607, // m/44'/607'
    rpcUrl: 'https://toncenter.com/api/v2',
    explorerUrl: 'https://tonscan.org',
    nativeCurrency: {
      name: 'Toncoin',
      symbol: 'TON',
      decimals: 9,
    },
    isEvm: false,
  },
  tron: {
    name: 'TRON',
    chainId: 728126428,
    coinType: 195, // m/44'/195'
    rpcUrl: 'https://api.trongrid.io',
    explorerUrl: 'https://tronscan.org',
    nativeCurrency: {
      name: 'TRON',
      symbol: 'TRX',
      decimals: 6,
    },
    isEvm: false,
  },
  solana: {
    name: 'Solana',
    chainId: 0,
    coinType: 501, // m/44'/501'
    rpcUrl: 'https://api.mainnet-beta.solana.com',
    explorerUrl: 'https://solscan.io',
    nativeCurrency: {
      name: 'Solana',
      symbol: 'SOL',
      decimals: 9,
    },
    isEvm: false,
  },
  spark: {
    name: 'Spark (Lightning)',
    chainId: 0,
    coinType: 998, // m/44'/998'
    rpcUrl: '',
    explorerUrl: '',
    nativeCurrency: {
      name: 'Bitcoin',
      symbol: 'BTC',
      decimals: 8,
    },
    isEvm: false,
  },
};

export const TESTNET_NETWORKS: Partial<Record<NetworkType, Partial<NetworkConfig>>> = {
  ethereum: {
    name: 'Sepolia',
    chainId: 11155111,
    // Using eth-sepolia.g.alchemy.com public endpoint (more reliable than rpc.sepolia.org)
    // Fallback order: 1. Alchemy public, 2. Infura public, 3. BlockPi
    rpcUrl: 'https://ethereum-sepolia-rpc.publicnode.com',
    explorerUrl: 'https://sepolia.etherscan.io',
  },
  solana: {
    name: 'Solana Devnet',
    rpcUrl: 'https://api.devnet.solana.com',
    explorerUrl: 'https://solscan.io?cluster=devnet',
  },
};

// BIP-44 derivation paths
export const DERIVATION_PATHS: Record<NetworkType, string> = {
  bitcoin: "m/44'/0'/0'/0",
  ethereum: "m/44'/60'/0'/0",
  ton: "m/44'/607'/0'/0",
  tron: "m/44'/195'/0'/0",
  solana: "m/44'/501'/0'/0",
  spark: "m/44'/998'/0'/0",
};

export function getNetworkConfig(
  network: NetworkType,
  isTestnet: boolean = false
): NetworkConfig {
  const mainnetConfig = NETWORKS[network];
  if (!isTestnet) return mainnetConfig;

  const testnetOverrides = TESTNET_NETWORKS[network];
  if (!testnetOverrides) return mainnetConfig;

  return {
    ...mainnetConfig,
    ...testnetOverrides,
  };
}
